const DXF_PARSER_SCRIPT_PATH = 'js/vendor/cad/dxf-parser/dxf-parser.js';
const DWG_CONVERTER_MODULE_PATH = 'js/vendor/cad/dwgdxf/index.js';
const DWG_CONVERTER_WASM_PATH = 'js/vendor/cad/dwgdxf/wasm';

const DXF_UNIT_DEFINITIONS = Object.freeze([
  { code: 'auto', label: 'Auto (aus CAD-Datei)', millimetersPerUnit: null },
  { code: 'mm', label: 'Millimeter', millimetersPerUnit: 1 },
  { code: 'cm', label: 'Zentimeter', millimetersPerUnit: 10 },
  { code: 'm', label: 'Meter', millimetersPerUnit: 1000 },
  { code: 'in', label: 'Zoll', millimetersPerUnit: 25.4 },
  { code: 'ft', label: 'Fuss', millimetersPerUnit: 304.8 }
]);

const DXF_INSUNITS_TO_MILLIMETERS = Object.freeze({
  1: 25.4,
  2: 304.8,
  3: 1609344,
  4: 1,
  5: 10,
  6: 1000,
  7: 1000000,
  8: 0.0000254,
  9: 0.0254,
  10: 914.4,
  11: 0.0000001,
  12: 0.000001,
  13: 0.001,
  14: 100,
  15: 10000,
  16: 100000,
  17: 1000000000,
  18: 149597870700000,
  19: 9.4607304725808e18,
  20: 3.085677581491367e19,
  21: 304.8006096012192,
  22: 25.4000508001016,
  23: 914.4018288036576,
  24: 1609347.2186944373
});

let dxfParserPromise = null;
let dwgConverterPromise = null;

function appRuntimeUrl(path) {
  if (typeof document === 'undefined') return path;
  return new URL(path, document.baseURI).href;
}

function loadDxfParser() {
  if (globalThis.DxfParser) return Promise.resolve(globalThis.DxfParser);
  if (dxfParserPromise) return dxfParserPromise;
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('DXF-Parser kann ausserhalb des Browsers nicht geladen werden.'));
  }
  dxfParserPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-cad-dxf-parser]');
    const script = existing || document.createElement('script');
    const finish = () => {
      if (globalThis.DxfParser) resolve(globalThis.DxfParser);
      else reject(new Error('DXF-Parser wurde geladen, ist aber nicht verfügbar.'));
    };
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', () => reject(new Error('DXF-Parser konnte nicht geladen werden.')), { once: true });
    if (!existing) {
      script.dataset.cadDxfParser = 'true';
      script.src = appRuntimeUrl(DXF_PARSER_SCRIPT_PATH);
      document.head.appendChild(script);
    }
  }).catch(error => {
    dxfParserPromise = null;
    throw error;
  });
  return dxfParserPromise;
}

function loadDwgConverter() {
  if (dwgConverterPromise) return dwgConverterPromise;
  const moduleUrl = appRuntimeUrl(DWG_CONVERTER_MODULE_PATH);
  dwgConverterPromise = import(/* @vite-ignore */ moduleUrl).catch(error => {
    dwgConverterPromise = null;
    throw new Error(`DWG-Konverter konnte nicht geladen werden: ${error?.message || error}`);
  });
  return dwgConverterPromise;
}

function parseDxfText(text, ParserConstructor) {
  const source = String(text || '').replace(/^\uFEFF/, '');
  if (!source.trim()) throw new Error('Die CAD-Datei ist leer.');
  const Parser = ParserConstructor || globalThis.DxfParser;
  if (typeof Parser !== 'function') throw new Error('DXF-Parser ist nicht verfügbar.');
  const drawing = new Parser().parseSync(source);
  if (!drawing || !Array.isArray(drawing.entities)) {
    throw new Error('Die DXF-Zeichnung enthält keinen lesbaren Modellbereich.');
  }
  return drawing;
}

async function convertDwgBytesToDxfText(bytes) {
  const converter = await loadDwgConverter();
  const result = await converter.convertDwgToDxf(bytes, {
    wasmBase: appRuntimeUrl(DWG_CONVERTER_WASM_PATH)
  });
  const text = new TextDecoder('utf-8').decode(result);
  if (!/\bSECTION\b/i.test(text)) {
    throw new Error('Die DWG-Datei konnte nicht in eine lesbare DXF-Zeichnung umgewandelt werden.');
  }
  return text;
}

function finitePoint(point) {
  const x = Number(point?.x);
  const y = Number(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return { x, y };
}

function identityMatrix() {
  return { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
}

function multiplyMatrices(parent, local) {
  return {
    a: parent.a * local.a + parent.c * local.b,
    b: parent.b * local.a + parent.d * local.b,
    c: parent.a * local.c + parent.c * local.d,
    d: parent.b * local.c + parent.d * local.d,
    e: parent.a * local.e + parent.c * local.f + parent.e,
    f: parent.b * local.e + parent.d * local.f + parent.f
  };
}

function transformPoint(point, matrix) {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.e,
    y: matrix.b * point.x + matrix.d * point.y + matrix.f
  };
}

function escapeSvgText(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function decodeCadText(value, actualMeasurement = '') {
  let text = String(value ?? '');
  if (actualMeasurement) text = text.replaceAll('<>', actualMeasurement);
  text = text
    .replace(/%%[dD]/g, '°')
    .replace(/%%[pP]/g, '±')
    .replace(/%%[cC]/g, 'Ø')
    .replace(/\\U\+([0-9a-fA-F]{4,6})/g, (match, code) => {
      try {
        return String.fromCodePoint(Number.parseInt(code, 16));
      } catch {
        return match;
      }
    })
    .replace(/\\S([^;]+);/g, (match, stacked) => stacked.replace(/[#^]/g, '/'))
    .replace(/\\[PX]/g, '\n')
    .replace(/\\~/g, ' ')
    .replace(/\\(?:A|C|F|H|Q|T|W)[^;]*;/gi, '')
    .replace(/\\[LlOoKk]/g, '')
    .replace(/[{}]/g, '')
    .replace(/\\\\/g, '\\');
  return text.split('\n').map(line => line.trim()).join('\n').trim();
}

function dimensionMeasurementText(entity) {
  const measurement = Number(entity?.actualMeasurement);
  const measuredText = Number.isFinite(measurement) ? compactNumber(measurement) : '';
  const override = String(entity?.text ?? '');
  if (override === ' ') return '';
  return decodeCadText(override || '<>', measuredText);
}

function textRotationRadians(entity) {
  const direction = finitePoint(entity?.directionVector);
  if (direction && Math.hypot(direction.x, direction.y) > 1e-9) {
    return Math.atan2(direction.y, direction.x);
  }
  const degrees = Number(entity?.rotation || 0);
  return Number.isFinite(degrees) ? degrees * Math.PI / 180 : 0;
}

function textAnchor(entity) {
  if (String(entity?.type || '').toUpperCase() === 'MTEXT') {
    const column = ((Math.max(1, Math.min(9, Number(entity.attachmentPoint) || 1)) - 1) % 3) + 1;
    return column === 2 ? 'middle' : (column === 3 ? 'end' : 'start');
  }
  const alignment = Number(entity?.halign) || 0;
  return [1, 4].includes(alignment) ? 'middle' : ([2, 5].includes(alignment) ? 'end' : 'start');
}

function textTopOffset(entity, blockHeight, textHeight) {
  if (String(entity?.type || '').toUpperCase() === 'MTEXT') {
    const row = Math.ceil(Math.max(1, Math.min(9, Number(entity.attachmentPoint) || 1)) / 3);
    if (row === 2) return -blockHeight / 2;
    if (row === 3) return -blockHeight;
    return 0;
  }
  const verticalAlignment = Number(entity?.valign) || 0;
  if (verticalAlignment === 3) return 0;
  if (verticalAlignment === 2) return -blockHeight / 2;
  if (verticalAlignment === 1) return -blockHeight;
  return -textHeight * 0.82;
}

function cadTextDescriptor(entity, parentMatrix, actualMeasurement = '', fallbackHeight = 0) {
  const type = String(entity?.type || '').toUpperCase();
  const content = decodeCadText(entity?.text, actualMeasurement);
  if (!content) return null;
  const alignedText = type === 'TEXT' && (Number(entity.halign) || Number(entity.valign));
  const position = finitePoint(type === 'MTEXT'
    ? entity.position
    : (alignedText ? entity.endPoint : entity.startPoint))
    || finitePoint(entity.position)
    || finitePoint(entity.startPoint);
  const explicitHeight = Number(type === 'MTEXT' ? entity.height : entity.textHeight);
  const height = Number.isFinite(explicitHeight) && explicitHeight > 0
    ? explicitHeight
    : Number(fallbackHeight);
  if (!position || !Number.isFinite(height) || height <= 0) return null;

  const lines = content.split('\n').filter((line, index, all) => line || all.length === 1 || index < all.length - 1);
  const xScaleValue = Number(entity?.xScale);
  const xScale = Number.isFinite(xScaleValue) && Math.abs(xScaleValue) > 1e-9 ? xScaleValue : 1;
  const angle = textRotationRadians(entity);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // SVG glyph coordinates point downwards. This local matrix maps them into
  // CAD's upward-pointing coordinate system before the whole drawing is
  // flipped back into SVG screen coordinates.
  const localMatrix = {
    a: cos * xScale,
    b: sin * xScale,
    c: sin,
    d: -cos,
    e: position.x,
    f: position.y
  };
  const matrix = multiplyMatrices(parentMatrix, localMatrix);
  const lineAdvance = height * 1.2;
  const blockHeight = height + Math.max(0, lines.length - 1) * lineAdvance;
  const maxCharacters = Math.max(1, ...lines.map(line => Array.from(line).length));
  const estimatedWidth = Math.max(height * 0.6, maxCharacters * height * 0.62);
  const anchor = textAnchor(entity);
  const left = anchor === 'middle' ? -estimatedWidth / 2 : (anchor === 'end' ? -estimatedWidth : 0);
  const top = textTopOffset(entity, blockHeight, height);
  const bounds = [
    { x: left, y: top },
    { x: left + estimatedWidth, y: top },
    { x: left + estimatedWidth, y: top + blockHeight },
    { x: left, y: top + blockHeight }
  ].map(point => transformPoint(point, matrix));
  return {
    lines,
    height,
    lineAdvance,
    firstBaseline: top + height * 0.82,
    anchor,
    matrix,
    bounds
  };
}

function insertionMatrix(entity, block, offset = { x: 0, y: 0 }) {
  const position = finitePoint(entity.position) || { x: 0, y: 0 };
  const base = finitePoint(block?.position) || { x: 0, y: 0 };
  const scaleX = Number.isFinite(Number(entity.xScale)) ? Number(entity.xScale) : 1;
  const scaleY = Number.isFinite(Number(entity.yScale)) ? Number(entity.yScale) : scaleX;
  const angle = Number(entity.rotation || 0) * Math.PI / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const translatedBaseX = (base.x - offset.x) * scaleX;
  const translatedBaseY = (base.y - offset.y) * scaleY;
  return {
    a: cos * scaleX,
    b: sin * scaleX,
    c: -sin * scaleY,
    d: cos * scaleY,
    e: position.x - cos * translatedBaseX + sin * translatedBaseY,
    f: position.y - sin * translatedBaseX - cos * translatedBaseY
  };
}

function sampledArc(centerValue, radiusValue, startValue, deltaValue) {
  const center = finitePoint(centerValue);
  const radius = Math.abs(Number(radiusValue));
  const start = Number(startValue);
  const delta = Number(deltaValue);
  if (!center || !Number.isFinite(radius) || radius <= 0 || !Number.isFinite(start) || !Number.isFinite(delta)) return [];
  const count = Math.max(8, Math.min(360, Math.ceil(Math.abs(delta) / (Math.PI / 36))));
  return Array.from({ length: count + 1 }, (_, index) => {
    const angle = start + delta * index / count;
    return { x: center.x + Math.cos(angle) * radius, y: center.y + Math.sin(angle) * radius };
  });
}

function sampledBulge(startValue, endValue, bulgeValue) {
  const start = finitePoint(startValue);
  const end = finitePoint(endValue);
  const bulge = Number(bulgeValue);
  if (!start || !end) return [];
  if (!Number.isFinite(bulge) || Math.abs(bulge) < 1e-9) return [start, end];
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-9) return [start, end];
  const centerOffset = chord * (1 - bulge * bulge) / (4 * bulge);
  const center = {
    x: (start.x + end.x) / 2 - dy / chord * centerOffset,
    y: (start.y + end.y) / 2 + dx / chord * centerOffset
  };
  const startAngle = Math.atan2(start.y - center.y, start.x - center.x);
  const delta = 4 * Math.atan(bulge);
  return sampledArc(center, Math.hypot(start.x - center.x, start.y - center.y), startAngle, delta);
}

function sampledPolyline(entity) {
  const vertices = Array.isArray(entity.vertices) ? entity.vertices.map(finitePoint).filter(Boolean) : [];
  if (vertices.length < 2) return [];
  const closed = entity.shape === true;
  const segmentCount = closed ? vertices.length : vertices.length - 1;
  const result = [];
  for (let index = 0; index < segmentCount; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    const segment = sampledBulge(start, end, entity.vertices[index]?.bulge);
    if (result.length && segment.length) segment.shift();
    result.push(...segment);
  }
  return result;
}

function sampledEllipse(entity) {
  const center = finitePoint(entity.center);
  const major = finitePoint(entity.majorAxisEndPoint);
  const ratio = Math.abs(Number(entity.axisRatio));
  if (!center || !major || !Number.isFinite(ratio) || ratio <= 0) return [];
  const start = Number.isFinite(Number(entity.startAngle)) ? Number(entity.startAngle) : 0;
  let end = Number.isFinite(Number(entity.endAngle)) ? Number(entity.endAngle) : Math.PI * 2;
  while (end <= start) end += Math.PI * 2;
  const count = Math.max(24, Math.min(360, Math.ceil((end - start) / (Math.PI / 36))));
  return Array.from({ length: count + 1 }, (_, index) => {
    const angle = start + (end - start) * index / count;
    return {
      x: center.x + major.x * Math.cos(angle) - major.y * ratio * Math.sin(angle),
      y: center.y + major.y * Math.cos(angle) + major.x * ratio * Math.sin(angle)
    };
  });
}

function entityPointPaths(entity) {
  switch (String(entity?.type || '').toUpperCase()) {
    case 'LINE':
      return [Array.isArray(entity.vertices) ? entity.vertices.map(finitePoint).filter(Boolean) : []];
    case 'LWPOLYLINE':
    case 'POLYLINE':
      return [sampledPolyline(entity)];
    case 'ARC': {
      let delta = Number(entity.angleLength);
      if (!Number.isFinite(delta)) {
        delta = Number(entity.endAngle) - Number(entity.startAngle);
        while (delta <= 0) delta += Math.PI * 2;
      }
      return [sampledArc(entity.center, entity.radius, entity.startAngle, delta)];
    }
    case 'CIRCLE':
      return [sampledArc(entity.center, entity.radius, 0, Math.PI * 2)];
    case 'ELLIPSE':
      return [sampledEllipse(entity)];
    case 'SPLINE': {
      const points = entity.fitPoints?.length ? entity.fitPoints : entity.controlPoints;
      return [Array.isArray(points) ? points.map(finitePoint).filter(Boolean) : []];
    }
    case 'SOLID':
      return [Array.isArray(entity.points) ? entity.points.map(finitePoint).filter(Boolean) : []];
    case '3DFACE':
      return [Array.isArray(entity.vertices) ? entity.vertices.map(finitePoint).filter(Boolean) : []];
    default:
      return [];
  }
}

function drawingLayer(drawing, layerName) {
  return drawing?.tables?.layer?.layers?.[layerName]
    || drawing?.tables?.layers?.[layerName]
    || null;
}

function collectCadPaths(drawing) {
  const paths = [];
  const filledPaths = [];
  const texts = [];
  const unsupportedTypes = new Set();
  let sourceEntityCount = 0;

  function visitEntities(entities, parentMatrix, depth = 0, actualMeasurement = '') {
    if (!Array.isArray(entities) || depth > 12) return;
    for (const entity of entities) {
      const layer = drawingLayer(drawing, entity?.layer);
      if (!entity || entity.visible === false || layer?.visible === false || layer?.frozen === true) continue;
      sourceEntityCount += 1;
      const type = String(entity.type || '').toUpperCase();
      if (type === 'INSERT' || type === 'DIMENSION') {
        const blockName = type === 'INSERT' ? entity.name : entity.block;
        const block = drawing?.blocks?.[blockName];
        if (!block?.entities) {
          unsupportedTypes.add(type);
          continue;
        }
        const columns = Math.max(1, Math.min(100, Math.round(Number(entity.columnCount) || 1)));
        const rows = Math.max(1, Math.min(100, Math.round(Number(entity.rowCount) || 1)));
        const dimensionText = type === 'DIMENSION' ? dimensionMeasurementText(entity) : actualMeasurement;
        const textCountBeforeBlock = texts.length;
        for (let row = 0; row < rows; row += 1) {
          for (let column = 0; column < columns; column += 1) {
            const local = insertionMatrix(entity, block, {
              x: column * (Number(entity.columnSpacing) || 0),
              y: row * (Number(entity.rowSpacing) || 0)
            });
            visitEntities(block.entities, multiplyMatrices(parentMatrix, local), depth + 1, dimensionText);
          }
        }
        // Some exporters omit the MTEXT/TEXT member from the anonymous
        // dimension block. The DIMENSION record still contains both its
        // measured value and intended text position, so preserve that value
        // instead of showing extension lines without a label.
        if (type === 'DIMENSION' && dimensionText && texts.length === textCountBeforeBlock) {
          const span = Math.abs(Number(entity.actualMeasurement)) || 100;
          const headerHeight = Number(drawing?.header?.$DIMTXT);
          const fallbackHeight = Number.isFinite(headerHeight) && headerHeight > 0
            ? headerHeight
            : Math.max(1, span / 40);
          const fallbackText = cadTextDescriptor({
            type: 'MTEXT',
            text: dimensionText,
            position: entity.middleOfText || entity.anchorPoint,
            height: fallbackHeight,
            attachmentPoint: Number(entity.attachmentPoint) || 5,
            rotation: entity.angle
          }, parentMatrix);
          if (fallbackText) texts.push(fallbackText);
        }
        continue;
      }
      if (type === 'TEXT' || type === 'MTEXT' || type === 'ATTRIB' || type === 'ATTDEF') {
        const text = cadTextDescriptor(entity, parentMatrix, actualMeasurement);
        if (text) texts.push(text);
        continue;
      }
      const pointPaths = entityPointPaths(entity);
      if (!pointPaths.length) {
        if (type !== 'POINT') unsupportedTypes.add(type || 'UNBEKANNT');
        continue;
      }
      for (const pointPath of pointPaths) {
        const transformed = pointPath.map(point => transformPoint(point, parentMatrix)).filter(finitePoint);
        if (transformed.length < 2) continue;
        if (type === 'SOLID' || type === '3DFACE') filledPaths.push(transformed);
        else paths.push(transformed);
      }
    }
  }

  visitEntities(drawing?.entities, identityMatrix());
  return {
    paths,
    filledPaths,
    texts,
    sourceEntityCount,
    unsupportedTypes: Array.from(unsupportedTypes).sort()
  };
}

function compactNumber(value) {
  const rounded = Math.round(Number(value) * 1000) / 1000;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}

function cadDrawingToSvg(drawing) {
  const collected = collectCadPaths(drawing);
  const points = [
    ...collected.paths.flat(),
    ...collected.filledPaths.flat(),
    ...collected.texts.flatMap(text => text.bounds)
  ];
  if (!points.length) throw new Error('Die CAD-Datei enthält keine unterstützten 2D-Zeichnungselemente.');
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const point of points) {
    minX = Math.min(minX, point.x);
    maxX = Math.max(maxX, point.x);
    minY = Math.min(minY, point.y);
    maxY = Math.max(maxY, point.y);
  }
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  if (!Number.isFinite(contentWidth) || contentWidth <= 0 || !Number.isFinite(contentHeight) || contentHeight <= 0) {
    throw new Error('Die CAD-Zeichnung besitzt keine zweidimensionale Ausdehnung.');
  }
  const strokeWidth = Math.max(contentWidth, contentHeight) / 1400;
  const padding = Math.max(strokeWidth * 4, Math.max(contentWidth, contentHeight) * 0.002);
  const widthUnits = contentWidth + padding * 2;
  const heightUnits = contentHeight + padding * 2;
  const screenPoint = point => ({
    x: point.x - minX + padding,
    y: maxY - point.y + padding
  });
  const pathData = collected.paths.map(path => path.map((point, index) => {
    const screen = screenPoint(point);
    return `${index === 0 ? 'M' : 'L'}${compactNumber(screen.x)} ${compactNumber(screen.y)}`;
  }).join('')).join('');
  const filledPathData = collected.filledPaths.map(path => path.map((point, index) => {
    const screen = screenPoint(point);
    return `${index === 0 ? 'M' : 'L'}${compactNumber(screen.x)} ${compactNumber(screen.y)}`;
  }).join('') + 'Z').join('');
  const screenMatrix = { a: 1, b: 0, c: 0, d: -1, e: -minX + padding, f: maxY + padding };
  const textData = collected.texts.map(text => {
    const matrix = multiplyMatrices(screenMatrix, text.matrix);
    const matrixValue = [matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f]
      .map(compactNumber)
      .join(' ');
    const lines = text.lines.map((line, index) => {
      const baseline = text.firstBaseline + index * text.lineAdvance;
      return `<tspan x="0" y="${compactNumber(baseline)}">${escapeSvgText(line)}</tspan>`;
    }).join('');
    return `<text transform="matrix(${matrixValue})" font-family="Arial,Helvetica,sans-serif" font-size="${compactNumber(text.height)}" text-anchor="${text.anchor}" fill="#111827">${lines}</text>`;
  }).join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${compactNumber(widthUnits)} ${compactNumber(heightUnits)}" preserveAspectRatio="none"><path d="${pathData}" fill="none" stroke="#111827" stroke-width="${compactNumber(strokeWidth)}" stroke-linecap="round" stroke-linejoin="round"/>${filledPathData ? `<path d="${filledPathData}" fill="#111827" stroke="#111827" stroke-width="${compactNumber(strokeWidth)}" stroke-linejoin="round"/>` : ''}${textData}</svg>`;
  return {
    svg,
    url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    widthUnits,
    heightUnits,
    bounds: { minX, minY, maxX, maxY },
    pathCount: collected.paths.length + collected.filledPaths.length,
    textCount: collected.texts.length,
    entityCount: collected.sourceEntityCount,
    unsupportedTypes: collected.unsupportedTypes
  };
}

function dxfInsunits(drawing) {
  const value = Number(drawing?.header?.$INSUNITS);
  return Number.isInteger(value) ? value : 0;
}

function cadMillimetersPerUnit(drawing, unitCode = 'auto') {
  const requested = DXF_UNIT_DEFINITIONS.find(unit => unit.code === unitCode);
  if (requested?.millimetersPerUnit) return requested.millimetersPerUnit;
  return DXF_INSUNITS_TO_MILLIMETERS[dxfInsunits(drawing)] || 1;
}

function detectedCadUnitLabel(drawing) {
  const factor = DXF_INSUNITS_TO_MILLIMETERS[dxfInsunits(drawing)];
  const definition = DXF_UNIT_DEFINITIONS.find(unit => unit.millimetersPerUnit === factor);
  return definition?.label || 'Millimeter (Fallback)';
}

async function readCadFloorplanFile(file) {
  if (!file) throw new Error('Keine CAD-Datei gewählt.');
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  if (!['dxf', 'dwg'].includes(extension)) throw new Error('Bitte eine DXF- oder DWG-Datei wählen.');
  const Parser = await loadDxfParser();
  let dxfText;
  if (extension === 'dwg') {
    dxfText = await convertDwgBytesToDxfText(new Uint8Array(await file.arrayBuffer()));
  } else {
    dxfText = await file.text();
  }
  const drawing = parseDxfText(dxfText, Parser);
  return {
    fileName: file.name,
    sourceFormat: extension,
    drawing,
    vector: cadDrawingToSvg(drawing)
  };
}

export {
  DXF_UNIT_DEFINITIONS,
  cadDrawingToSvg,
  cadMillimetersPerUnit,
  detectedCadUnitLabel,
  parseDxfText,
  readCadFloorplanFile
};
